import { LayoutMatrix, parseSketch, LayoutSketch, Point, drawSketch } from "../common";
import { BlockModel } from "./BlockModel";

export class BoardModel {
  private occupancy: LayoutMatrix;
  private readonly width: number;
  private readonly height: number;

  static buildEmptyBoard(width: number, height: number): BoardModel {
    const occupancy: LayoutMatrix = [];
    let row;
    for(let i=0; i < height; i += 1) {
      occupancy.push(row = []);
      for(let j=0; j < width; j += 1) {
        row.push(false);
      }
    }
    return new BoardModel(occupancy);
  }

  static buildFromSketch(sketch: LayoutSketch): BoardModel {
    return new BoardModel(parseSketch(sketch));
  };

  constructor(occupancy: LayoutMatrix) {
    this.occupancy = occupancy;
    this.height = occupancy.length;
    this.width = occupancy[0].length;
  }

  draw(): LayoutSketch {
    return drawSketch(this.occupancy);
  }

  doesBlockFit(x: number, y: number, block: BlockModel): boolean {
    if (this.isBlockWithinBoard(x, y, block) && !this.doesBlockOverlapAnother(x, y, block)) {
      return true;
    } else {
      return false;
    }
  }

  placeBlock(x: number, y: number, block: BlockModel): void {
    const points: Point[] = block.points();
    for(const point of points) {
      this.occupySpace(x + point.x, y + point.y);
    };
  }

  completeRows(): number[] {
    const rowIndexes: number[] = [];
    this.occupancy.forEach((row, index) => {
      if (row.reduce((a, b) => (a && b), true)) {
        rowIndexes.push(index);
      }
    });
    return rowIndexes;
  }

  removeRows(rows: number[]): void {
    this.occupancy = rows.reduce((occAcc, row) => this.removeRow(occAcc, row), this.occupancy);
  }

  private isBlockWithinBoard(x: number, y: number, block: BlockModel) {
    return (
      x >= 0 &&
      y >= 0 && 
      x <= this.width - block.width() &&
      y <= this.height - block.height()
    );
  }

  private doesBlockOverlapAnother(x: number, y: number, block: BlockModel) {
    const points: Point[] = block.points();
    for(const point of points) {
      const occupied = this.isBoardSpaceOccupied(x + point.x, y + point.y)
      if (occupied) {
        return true;
      }
    };
    return false;
  }

  private isBoardSpaceOccupied(x: number, y: number): boolean {
    const row = this.occupancy[y];
    return row && row[x];
  }

  private occupySpace(x: number, y: number): void {
    const row = this.occupancy[y];
    if (row) {
      row[x] = true;
    }
  }

  private removeRow(occupancy: LayoutMatrix, rowIndex: number): LayoutMatrix {
    const height = occupancy.length;
    const width = occupancy[0].length;
    if (rowIndex < 0 || rowIndex >= height) {
      throw new Error("cant remove out of bounds row");
    }
    const emptyRow: boolean[] = Array(width).fill(false);
    const newOccupancy = [emptyRow];
    for(let i=0; i<height; i+=1) {
      if (i !== rowIndex) {
        newOccupancy.push(occupancy[i]);
      }
    }
    return newOccupancy;
  }
}
